import type { Disposable } from "@/shared/contracts/lifecycle";

export const HOVER_IDLE_AUTOSAVE_INTERVAL_MS = 30_000;

export interface HoverIdleAutosaveController extends Disposable {
  saveAfterImportantEvent(): void;
}

export interface HoverIdleAutosaveOptions {
  readonly saveNow: () => void | Promise<void>;
  readonly intervalMs?: number;
  readonly saveOnVisibilityHidden?: boolean;
  readonly visibilitySource?: HoverIdleVisibilitySource;
  readonly beforeUnloadTarget?: HoverIdleEventTarget;
  readonly setIntervalFn?: (
    handler: () => void,
    timeoutMs: number,
  ) => ReturnType<typeof setInterval>;
  readonly clearIntervalFn?: (
    intervalId: ReturnType<typeof setInterval>,
  ) => void;
}

export interface HoverIdleEventTarget {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

export interface HoverIdleVisibilitySource extends HoverIdleEventTarget {
  readonly visibilityState: DocumentVisibilityState;
}

export function createHoverIdleAutosaveController(
  options: HoverIdleAutosaveOptions,
): HoverIdleAutosaveController {
  const intervalMs = options.intervalMs ?? HOVER_IDLE_AUTOSAVE_INTERVAL_MS;
  const setIntervalFn = options.setIntervalFn ?? globalThis.setInterval;
  const clearIntervalFn = options.clearIntervalFn ?? globalThis.clearInterval;
  const visibilitySource =
    options.visibilitySource ??
    (typeof document === "undefined" ? null : document);
  const beforeUnloadTarget =
    options.beforeUnloadTarget ??
    (typeof window === "undefined" ? null : window);
  let isDisposed = false;
  let isImportantSaveInFlight = false;
  let isImportantSaveQueued = false;

  const saveNow = (): void => {
    if (isDisposed) {
      return;
    }

    void options.saveNow();
  };

  const requestImportantSave = (): void => {
    if (isDisposed) {
      return;
    }
    if (isImportantSaveInFlight) {
      isImportantSaveQueued = true;
      return;
    }

    isImportantSaveInFlight = true;
    let result: void | Promise<void>;
    try {
      result = options.saveNow();
    } catch (error) {
      isImportantSaveInFlight = false;
      throw error;
    }

    const finish = (): void => {
      isImportantSaveInFlight = false;
      if (isDisposed || !isImportantSaveQueued) {
        return;
      }
      isImportantSaveQueued = false;
      requestImportantSave();
    };
    void Promise.resolve(result).then(finish, finish);
  };

  const handleVisibilityChange = (): void => {
    if (visibilitySource === null) {
      return;
    }

    if (
      options.saveOnVisibilityHidden !== false &&
      visibilitySource.visibilityState === "hidden"
    ) {
      saveNow();
    }
  };

  const handleBeforeUnload = (): void => {
    saveNow();
  };

  const intervalId = setIntervalFn(saveNow, intervalMs);
  visibilitySource?.addEventListener(
    "visibilitychange",
    handleVisibilityChange,
  );
  beforeUnloadTarget?.addEventListener("beforeunload", handleBeforeUnload);

  return {
    saveAfterImportantEvent(): void {
      requestImportantSave();
    },

    dispose(): void {
      if (isDisposed) {
        return;
      }

      isDisposed = true;
      isImportantSaveQueued = false;
      clearIntervalFn(intervalId);
      visibilitySource?.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
      beforeUnloadTarget?.removeEventListener(
        "beforeunload",
        handleBeforeUnload,
      );
    },
  };
}
